import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useDevice, useSnapshot } from './state';

export function Page({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <main className="page">
      <header className="topbar">
        <Link to="/" className="brand" aria-label="Curentia home">
          <img src="/brand/curentia-logo.png" alt="Curentia" width={138} height={51} />
        </Link>
        {actions}
      </header>
      {children}
    </main>
  );
}

/** The lock button shown on every unlocked screen. */
export function LockButton() {
  const device = useDevice();
  return (
    <button type="button" className="button secondary small" onClick={() => device.lock()}>
      Lock
    </button>
  );
}

export function ConnectionStatus() {
  const { online, live } = useSnapshot();
  return (
    <p className="status" aria-live="polite">
      <span className={`dot ${online ? 'on' : ''}`} aria-hidden="true" />
      {online ? (live ? 'Connected, live updates on' : 'Connected') : 'Offline. Your data on this device is still here.'}
    </p>
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string };

export function Field({ label, hint, type = 'text', ...input }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} {...(hint ? { 'aria-describedby': hintId } : {})} {...input} />
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function ErrorText({ error }: { error: string | null }) {
  return error ? (
    <p className="error" role="alert">
      {error}
    </p>
  ) : null;
}

export function Submit({ busy, children, busyText = 'Working…' }: { busy: boolean; children: ReactNode; busyText?: string }) {
  return (
    <button type="submit" className="button" disabled={busy} aria-busy={busy}>
      {busy ? busyText : children}
    </button>
  );
}

/** Shows +919876543210 as +91 98765 43210. */
export function formatMobile(mobile: string): string {
  const match = /^\+91(\d{5})(\d{5})$/.exec(mobile);
  return match ? `+91 ${match[1]} ${match[2]}` : mobile;
}

/** A code in groups that wrap only between groups. The text content stays the plain code. */
export function GroupedCode({
  code,
  separator = '-',
  breakAfter,
  label,
  testId,
}: {
  code: string;
  separator?: string;
  /** Start a new line after this many groups. */
  breakAfter?: number;
  label: string;
  testId: string;
}) {
  const groups = code.split(separator);
  return (
    <output className="code" aria-label={label} data-testid={testId}>
      {groups.map((group, i) => (
        <span key={i} className="code-group">
          {group}
          {i < groups.length - 1 ? separator : ''}
          {breakAfter && (i + 1) % breakAfter === 0 && i < groups.length - 1 ? <br /> : null}
        </span>
      ))}
    </output>
  );
}

export const MIN_PASSWORD = 10;

/** Checks a new password and its confirmation. Returns a message, or null when they are fine. */
export function passwordProblem(password: string, confirm: string): string | null {
  if (password.length < MIN_PASSWORD) return `Use at least ${MIN_PASSWORD} characters for your password.`;
  if (password !== confirm) return 'The two passwords do not match.';
  return null;
}
